import type { LucideIcon } from "lucide-react";
import { demoHiddenTabs, tabLabelKey, tabs, type Tab } from "../shell/navigation.js";

/*
 * The app's own pages, as one searchable index (#1037).
 *
 * The command palette (#501) used to carry a private copy of this — its own
 * `tabs` walk and its own substring/subsequence matcher — while the Search page
 * could not find a page at all. Folding the palette's Navigate group into site
 * search would otherwise have produced a SECOND copy rather than a merge, so
 * the index and the matcher live here and both surfaces read them:
 *
 *   - SearchView renders these beside the household results (see the ranking
 *     rule below),
 *   - CommandPalette stays as the Ctrl/Cmd-K fast path over the same index.
 *
 * The palette's quick actions ("Toggle theme", "Copy link to this page") are
 * deliberately NOT folded in: they are commands, not things the household owns,
 * and a results list that mixes "your milk" with "toggle theme" is the same
 * category error the ranking rule below exists to prevent. They stay palette-
 * only, which is why this module knows about pages and nothing else.
 */

/**
 * How well a query matched a page, best first.
 *
 * `subsequence` is the palette's "fp" -> "Floor Plan" trick. It is the weakest
 * tier on purpose: it matches almost anything given enough letters, so it must
 * never be what pushes a page above a household record.
 */
export type PageMatchTier = "exact" | "prefix" | "substring" | "subsequence";

const TIER_RANK: Record<PageMatchTier, number> = {
  exact: 0,
  prefix: 1,
  substring: 2,
  subsequence: 3
};

/**
 * The tiers that read as "take me to that page" rather than "I happen to share
 * some letters with that page". Only these promote a page above the household
 * results — see `pagesOutrankResults`.
 */
const NAVIGATIONAL_TIERS: ReadonlySet<PageMatchTier> = new Set<PageMatchTier>(["exact", "prefix"]);

export type PageEntry = {
  tab: Tab;
  path: string;
  /** The name the sidebar shows, in the reader's language. */
  label: string;
  /** The English source name, so a query typed in either language still hits. */
  sourceLabel: string;
  icon: LucideIcon;
};

export type PageMatch = PageEntry & { tier: PageMatchTier };

/** Site search shows at most this many pages; the rest of the screen is data. */
export const MAX_PAGE_RESULTS = 5;

/**
 * Lowercase and strip diacritics, so a Spanish reader typing "version" still
 * finds "Notas de la versión" and "huerto" is not gated on the accent keys.
 */
function fold(value: string): string {
  // The escape form, not the literal combining marks: those are invisible in a
  // diff and the first tool that re-encodes this file would silently eat them.
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

/** Word split that treats `-` and `/` as separators, so "/floor-plan" is three words. */
function words(value: string): string[] {
  return value.split(/[^\p{Letter}\p{Number}]+/u).filter(Boolean);
}

function isSubsequence(query: string, text: string): boolean {
  let index = 0;
  for (const char of text) {
    if (char === query[index]) {
      index += 1;
      if (index === query.length) return true;
    }
  }
  return false;
}

/**
 * Shorter than this, a query names nothing.
 *
 * One character starts a word in a third of the sidebar ("s" -> Search, Scan,
 * Shopping, Settings) and is a subsequence of nearly every page name, so the
 * two tiers that are allowed to READ as a name are gated on it — otherwise the
 * first keystroke paints a full page group on top of the household results.
 * A one-character query can still match, at `substring`; it just cannot claim
 * to have named the page. See `pagesOutrankResults`.
 */
const MIN_NAMING_QUERY = 2;

/**
 * The best tier at which `query` matches any of `candidates`, or null.
 *
 * Each candidate is one alternative NAME for the same page (the translated
 * label, the English source label, the tab id, the path). They are matched one
 * at a time and never as a joined blob: a query whose letters are spread ACROSS
 * two names has not matched either of them.
 *
 * Exported for the tests and for the palette's non-page items; page results go
 * through `matchPages`.
 */
export function pageMatchTier(query: string, candidates: readonly string[]): PageMatchTier | null {
  const q = fold(query);
  if (!q) return null;
  const folded = candidates.map(fold);
  if (folded.some((candidate) => candidate === q)) return "exact";
  // Whole-string prefix as well as word-start prefix. `words()` splits on every
  // non-alphanumeric, so a query that has reached its space ("home m") starts no
  // single word; without the whole-string test the page would drop below the
  // household results in the middle of typing its own name and climb back on
  // the last keystroke.
  if (q.length >= MIN_NAMING_QUERY
    && folded.some((candidate) => candidate.startsWith(q) || words(candidate).some((word) => word.startsWith(q)))) {
    return "prefix";
  }
  if (folded.some((candidate) => candidate.includes(q))) return "substring";
  // Per candidate, never over `folded.join(" ")`. Joining let a query collect a
  // letter from the label, another from the id and another from the path, which
  // made "eggs" a match for Settings ("settings settings settings settings")
  // and "leche" a match for the Home Map ("plano de la casa floor plan
  // floor-plan floor-plan") — ordinary household nouns, matching pages they
  // have nothing to do with.
  if (q.length >= MIN_NAMING_QUERY && folded.some((candidate) => isSubsequence(q, candidate))) return "subsequence";
  return null;
}

/**
 * Same matcher, boolean form, for list items that are not pages (the palette's
 * quick actions). An empty query matches everything, which is what an unfiltered
 * palette wants.
 */
export function fuzzyMatches(query: string, haystack: string): boolean {
  return !fold(query) || pageMatchTier(query, [haystack]) !== null;
}

/**
 * Every page a reader may navigate to, in sidebar order.
 *
 * `translate` is the SHELL namespace's `t`: listing pages from `tabs[].label`
 * (the English source array) is what put "Finance" in the palette while the nav
 * beside it said "Finanzas" (#1088). Demo mode hides the same tabs the sidebar
 * hides (#838) — search must not become the way around that.
 */
export function pageIndex(options: { translate: (key: string) => string; demoMode?: boolean }): PageEntry[] {
  return tabs
    .filter((tab) => !(options.demoMode && demoHiddenTabs.has(tab.id)))
    .map((tab) => ({
      tab: tab.id,
      path: tab.path,
      label: options.translate(tabLabelKey(tab.id)),
      sourceLabel: tab.label,
      icon: tab.icon
    }));
}

/**
 * Pages matching `query`, best tier first and sidebar order within a tier
 * (Array#sort is stable, so `entries` order carries through).
 */
export function matchPages(
  query: string,
  entries: readonly PageEntry[],
  limit: number = MAX_PAGE_RESULTS
): PageMatch[] {
  const matched: PageMatch[] = [];
  for (const entry of entries) {
    const tier = pageMatchTier(query, [entry.label, entry.sourceLabel, entry.tab, entry.path.replace(/^\//, "")]);
    if (tier) matched.push({ ...entry, tier });
  }
  return matched.sort((a, b) => TIER_RANK[a.tier] - TIER_RANK[b.tier]).slice(0, limit);
}

/**
 * THE RANKING RULE (#1037).
 *
 * Household data outranks the app's own pages, unless the query NAMES a page:
 * a page group goes above the results only when its best match is `exact` or
 * `prefix` — the query is the whole name, or the start of a word in it ("chores",
 * "map", "finan"). Anything weaker — a substring buried mid-word, or a
 * subsequence like "fp" — renders AFTER the household results.
 *
 * That is what keeps someone searching for their milk from being handed a
 * Settings page: an incidental letter overlap can never outrank a real record,
 * while "take me to Chores" still lands on top. When there are no data results
 * at all the pages are the only thing on screen either way, so the weak tiers
 * still work as navigation — they just never displace anything.
 */
export function pagesOutrankResults(matches: readonly PageMatch[]): boolean {
  const best = matches[0];
  return Boolean(best && NAVIGATIONAL_TIERS.has(best.tier));
}
